import { useState } from "react";
import "./book-cover.css";

interface BookCoverProps {
  /** Book id — the cover is streamed from our own origin via /api/cover/:id. */
  bookId: string;
  /** Whether the book has a cover at all; false renders the generated cover directly. */
  hasCover: boolean;
  title: string;
  author?: string | null;
  alt: string;
  className?: string;
}

// Dark jacket colours, all ≥4.5:1 against the cream text.
const JACKETS = ["#5C2730", "#3D2659", "#6A4C8C", "#24505A", "#2F4A36", "#6B3A1F"];

function jacketFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return JACKETS[h % JACKETS.length]!;
}

// Catalogue authors are stored "Surname, Given; Surname2, Given2" — a cover
// reads "Given Surname", so flip each and join.
function coverAuthor(author: string): string {
  return author
    .split(";")
    .map((a) => {
      const [last, first] = a.split(",").map((s) => s.trim());
      return first ? `${first} ${last}` : last;
    })
    .filter(Boolean)
    .join(" & ");
}

export function BookCover({ bookId, hasCover, title, author, alt, className }: BookCoverProps) {
  // Track failure per-id so navigating to another book auto-resets the fallback.
  const [failedId, setFailedId] = useState<string | null>(null);
  const failed = failedId === bookId;

  if (!hasCover || failed) {
    // No real cover: render a typographic jacket with the book's own title and
    // author, never a generic icon (Play rejected the 📖 as "incorrect placeholder").
    return (
      <div
        className={`${className ?? ""} generated-cover`}
        style={{ backgroundColor: jacketFor(bookId) }}
        role="img"
        aria-label={alt}
      >
        <div className="generated-cover__inner">
          <span className="generated-cover__title">{title}</span>
          {author && <span className="generated-cover__author">{coverAuthor(author)}</span>}
        </div>
      </div>
    );
  }
  return (
    <img
      src={`/api/cover/${bookId}`}
      alt={alt}
      loading="lazy"
      onError={() => setFailedId(bookId)}
    />
  );
}
